/**
 * Error boundary for CompositionWizard
 * Catches unexpected rendering errors and displays user-friendly message.
 */
import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  onClose: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class CompositionWizardErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("CompositionWizard error:", error.message, {
      componentStack: errorInfo.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          data-testid="composition-wizard-error"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={this.props.onClose}
            onKeyDown={(e) => e.key === "Escape" && this.props.onClose()}
            role="button"
            tabIndex={0}
            aria-label="Close wizard"
          />
          <div className="relative bg-sa-surface rounded-xl shadow-2xl p-8 max-w-md text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sa-danger/10 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-sa-danger"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-sa-text mb-2">Something went wrong</h3>
            <p className="text-sm text-sa-text-muted mb-6">
              An unexpected error occurred while rendering the composition wizard. Please try again.
            </p>
            <button
              onClick={this.props.onClose}
              className="px-6 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-lg transition-colors"
            >
              Close and Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
